import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IconText } from '~/src/dash/components/IconText';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  ICustomRenderCellJSXProps,
  IListViewTableDataCell,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { borderRadius, Panel, Panels, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateHasData } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  IPlatformConnectionWithNamesAndType,
  IPlatformConnectUiParams,
} from '~/src/dash/views/platform-connect/common';
import {
  formatDataToLVTRows,
  IConnectionsConfig,
} from '~/src/dash/views/platform-connect/Connections/config';
import { usePlatformConnectsStore } from '~/src/dash/views/platform-connect/contexts';

interface IConnectionsProps {
  connections: IPlatformConnectionWithNamesAndType[];
  fetchConnections: (params: IPlatformConnectUiParams) => void;
  cfg: IConnectionsConfig;
  updateCfg: (params: IPlatformConnectUiParams) => void;
  totalRows: number;
  isActionPending: boolean;
  loadState: LoadingState;
  handleClickOpenDrawer: (props: ICustomRenderCellJSXProps) => void;
  handleRenderStatus: (row: Record<string, IListViewTableDataCell>) => JSX.Element;
  handleGetCurrencyValue: (row: Record<string, IListViewTableDataCell>) => string;
}

export const Connections = ({
  connections,
  fetchConnections,
  cfg,
  isActionPending,
  totalRows,
  loadState,
  handleClickOpenDrawer,
  handleRenderStatus,
  handleGetCurrencyValue,
}: IConnectionsProps) => {
  const { session, isSuperUser } = useBaseStore();
  const { readAuthorization, writeAuthorization } = usePlatformConnectsStore();
  const { cypressClassName, defaultState } = cfg;

  const [currPage, setCurrPage] = useState(defaultState.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(defaultState.rowsPerPage);

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
    fetchConnections({
      rowsPerPage,
      currPage: page,
    });
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    fetchConnections({
      rowsPerPage: updatedRowsPerPage,
      currPage,
    });
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  const handleIsStatusDisabled = (props: ICustomRenderCellJSXProps) => {
    return !(
      props.row.ActionPublisherID.value === '0' ||
      props.row.ActionPublisherID.value === session.publisher.ID
    );
  };

  return loadState !== LoadingStateHasData ? (
    <></>
  ) : (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
      paddingTop={spacing.none}
    >
      {connections.length === 0 ? (
        <Panel>
          <Rows>
            <Row
              marginTop={spacing.small}
              style={{
                padding: '8px 16px',
                backgroundColor: 'rgba(93, 102, 250, 0.08)',
                width: 'fit-content',
                borderRadius: borderRadius.base,
              }}
            >
              <IconText
                small
                gap={spacing.small}
                icon='light-bulb'
                iconColor={publicaColors.blue}
                text='Active connections will show in this tab once you have accepted offers'
                style={{
                  padding: 'none',
                  marginTop: '1px',
                }}
              />
            </Row>
          </Rows>
        </Panel>
      ) : (
        <Panels max vertical relative paddingTop={spacing.none}>
          <Panel>
            <Row smallVerticalPadding>
              <Text small>Click any row to review connection details and manage connections.</Text>
            </Row>
          </Panel>
          <Panel highlighted largeHorizontalPadding verticalPadding>
            {/* TABLE */}
            <Rows>
              <Row noHorizontalPadding>
                <ListViewTable
                  customFunctions={{
                    handleClickOpenDrawer,
                    handleIsStatusDisabled,
                    handleRenderStatus,
                    handleGetCurrencyValue,
                  }}
                  enableCustomLeftColumn
                  activeColumnKeys={Object.values(
                    extractVisibleColumns(
                      cfg.table.columns,
                      defaultState.activeColumnKeys || [],
                      isSuperUser,
                    ),
                  )
                    .filter((column: any) => column.selected)
                    .map((column: any) => column.ID)}
                  columns={cfg.table.columns}
                  isExpanded={defaultState.isTableExpanded}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleOnClickColumnHeader={() => {
                    return;
                  }}
                  isActionPending={isActionPending}
                  isReadAuthorized={readAuthorization.isAuthorized}
                  isSuperUser={isSuperUser}
                  isWriteAuthorized={writeAuthorization.isAuthorized}
                  loadState={loadState}
                  order={defaultState.orderDirection}
                  orderBy={defaultState.orderBy}
                  page={currPage}
                  rows={formatDataToLVTRows(connections, cfg.table)}
                  rowsPerPage={rowsPerPage}
                  totalRowCount={totalRows}
                  className={cypressClassName}
                />
              </Row>
            </Rows>
          </Panel>
        </Panels>
      )}
    </Panels>
  );
};
